import produce from 'immer';
import { atom } from 'ximple';
import { BilberryGiftcardStatus, BilberryPromoCodeStatus } from '@repo/types';
import { VERSION } from '@repo/widget-env/__autogen/env';
import { LocalStorageKey, getDynamicStorageKey } from '../localstorage';

export type IgnoredValueCards = {
    valueCardIdsToIgnore: number[];
    valueCardProductIdsToIgnore: number[];
    valueCardProductTypeIdsToIgnore: number[];
};

type State = {
    promoCode: string;
    appliedPromoCode: BilberryPromoCodeStatus | null;
    giftcards: BilberryGiftcardStatus[];
    ignoredValueCards: IgnoredValueCards;
};

type SetPromoCodeAction = {
    type: 'setPromoCode';
    value: State['appliedPromoCode'];
};

type SetGiftcardsAction = {
    type: 'setGiftcards';
    value: State['giftcards'];
};

type SetIgnoredValueCardsAction = {
    type: 'setIgnoredValueCards';
    value: State['ignoredValueCards'];
};

type ResetIgnoredValueCardsAction = {
    type: 'resetIgnoredValueCards';
    value?: undefined;
};

type Actions =
    | SetPromoCodeAction
    | SetGiftcardsAction
    | SetIgnoredValueCardsAction
    | ResetIgnoredValueCardsAction;

export const voucherAtom = atom<State, Actions>({
    initialValue: {
        promoCode: '',
        appliedPromoCode: null,
        giftcards: [],
        ignoredValueCards: {
            valueCardIdsToIgnore: [],
            valueCardProductIdsToIgnore: [],
            valueCardProductTypeIdsToIgnore: [],
        },
    },
    persistKey: getDynamicStorageKey(LocalStorageKey.BILBERRY_VOUCHER),
    persistLayer: 'session',
    appVersion: VERSION,
    update: produce((state: State, { type, value }: Actions): State => {
        switch (type) {
            case 'setPromoCode':
                state.appliedPromoCode = value;
                state.promoCode = value ? value.coupon_code : '';
                break;
            case 'setGiftcards':
                state.giftcards = value;
                break;
            case 'setIgnoredValueCards':
                state.ignoredValueCards = value;
                break;
            case 'resetIgnoredValueCards':
                state.ignoredValueCards = {
                    valueCardIdsToIgnore: [],
                    valueCardProductIdsToIgnore: [],
                    valueCardProductTypeIdsToIgnore: [],
                };
                break;
        }

        return state;
    }),
});

export function setPromoCode(value: SetPromoCodeAction['value']): SetPromoCodeAction {
    return { type: 'setPromoCode', value };
}

export function resetPromoCode(): SetPromoCodeAction {
    return setPromoCode(null);
}

export function setGiftcards(value: SetGiftcardsAction['value']): SetGiftcardsAction {
    return { type: 'setGiftcards', value };
}

export function resetGiftcards(): SetGiftcardsAction {
    return setGiftcards([]);
}

export function setIgnoredValueCards(
    value: SetIgnoredValueCardsAction['value'],
): SetIgnoredValueCardsAction {
    return { type: 'setIgnoredValueCards', value };
}

export function resetIgnoredValueCards(): ResetIgnoredValueCardsAction {
    return { type: 'resetIgnoredValueCards' };
}

export function selectHasIgnoredValueCards({ ignoredValueCards }: State): boolean {
    return (
        (Object.keys(ignoredValueCards) as (keyof typeof ignoredValueCards)[]).flatMap(
            (i) => ignoredValueCards[i],
        ).length > 0
    );
}
